import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { MessageSquare, Mail, Copy, Loader2, RefreshCw, Smile, Zap, Handshake, FileText, Save, BadgeCheck } from 'lucide-react';
import TemplatePicker from './TemplatePicker';

/**
 * Reusable supplier message generator dialog.
 *
 * Props:
 *   open, onOpenChange — dialog state
 *   supplier — { name, moq, price_range_low/high, lead_time_days, risk_level, ... }
 *   context — { product_name, quantity, budget } (used for prompt + placeholder substitution)
 *   defaultLanguage — 'en' | 'fr'
 *   savedId — optional. If supplier is already saved, pass its saved-supplier id.
 *             Enables the "Mark as contacted" CTA after the user copies a message.
 *   savedStatus — optional current status of the saved supplier
 *   onStatusChanged — optional callback invoked after a successful status update
 */
const MessageDialog = ({ open, onOpenChange, supplier, context, defaultLanguage = 'en', savedId, savedStatus, onStatusChanged }) => {
  const { t } = useLanguage();

  const [aiMessage, setAiMessage] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [language, setLanguage] = useState(defaultLanguage);
  const [messageType, setMessageType] = useState('whatsapp');
  const [tone, setTone] = useState('polite');

  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [saveTplName, setSaveTplName] = useState('');
  const [saveTplCategory, setSaveTplCategory] = useState('general_intro');
  const [savingTpl, setSavingTpl] = useState(false);
  const [markingContacted, setMarkingContacted] = useState(false);
  const [hasMarked, setHasMarked] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) {
      setAiMessage('');
      setGenerating(false);
      setCopied(false);
      setLanguage(defaultLanguage);
      setMessageType('whatsapp');
      setTone('polite');
      setHasMarked(false);
    }
  }, [open, defaultLanguage]);

  if (!supplier) return null;

  const supplierContext = {
    supplier_name: supplier.name,
    product_name: context?.product_name,
    quantity: context?.quantity,
    budget: context?.budget,
    moq: supplier.moq,
    lead_time_days: supplier.lead_time_days,
    risk_level: supplier.risk_level,
  };

  const generate = async () => {
    setGenerating(true);
    setAiMessage('');
    setCopied(false);
    try {
      const { data } = await api.post('/api/generate-message', {
        supplier_name: supplier.name,
        product_name: context?.product_name,
        quantity: context?.quantity,
        budget: context?.budget,
        language,
        message_type: messageType,
        tone,
        moq: supplier.moq,
        price_range_low: supplier.price_range_low,
        price_range_high: supplier.price_range_high,
        lead_time_days: supplier.lead_time_days,
        risk_level: supplier.risk_level,
      });
      setAiMessage(data.message);
    } catch (e) {
      setAiMessage(t('failedToGenerate'));
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(aiMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openSaveTpl = () => {
    setSaveTplName(supplier ? `${context?.product_name || ''} — ${supplier.name}`.trim() : '');
    setSaveTplCategory('general_intro');
    setSaveTplOpen(true);
  };

  const saveAsTemplate = async () => {
    if (!saveTplName.trim() || !aiMessage.trim()) {
      toast.error(t('templateError'));
      return;
    }
    setSavingTpl(true);
    try {
      await api.post('/api/templates', {
        name: saveTplName.trim(),
        category: saveTplCategory,
        body: aiMessage,
        language,
        message_type: messageType,
        tone,
      });
      toast.success(t('templateSaved'));
      setSaveTplOpen(false);
    } catch (e) {
      toast.error(t('templateError'));
    } finally {
      setSavingTpl(false);
    }
  };

  const onApplyTemplate = ({ body }) => {
    setAiMessage(body);
    toast.success(t('templateApplied'));
  };

  const markAsContacted = async () => {
    if (!savedId || markingContacted) return;
    setMarkingContacted(true);
    try {
      const { data } = await api.put(`/api/saved-suppliers/${savedId}`, { status: 'contacted' });
      setHasMarked(true);
      toast.success(t('markedContacted'));
      if (onStatusChanged) onStatusChanged(data);
    } catch (e) {
      toast.error(t('templateError'));
    } finally {
      setMarkingContacted(false);
    }
  };

  const showMarkContactedCta = Boolean(savedId)
    && (!savedStatus || savedStatus === 'not_contacted')
    && !hasMarked
    && copied;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-lg p-0 gap-0 h-screen max-h-screen w-screen rounded-none inset-0 translate-x-0 translate-y-0 sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:h-auto sm:max-h-[90vh] sm:w-auto sm:rounded-lg overflow-hidden flex flex-col"
          data-testid="message-dialog"
        >
          <DialogHeader className="px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-200">
            <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              <span className="truncate">{t('whatsappMessageFor')} {supplier.name}</span>
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t('whatsappMessageFor')} {supplier.name}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPickerOpen(true)}
              className="w-full justify-start"
              data-testid="open-template-picker"
            >
              <FileText className="w-4 h-4 mr-2 text-blue-600" />
              {t('useTemplate')}
            </Button>

            <div className="space-y-3" data-testid="message-options">
              {/* Message type */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500">{t('messageType')}</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="message-type-group">
                  <Button
                    type="button"
                    variant={messageType === 'whatsapp' ? 'default' : 'outline'}
                    onClick={() => { setMessageType('whatsapp'); setAiMessage(''); }}
                    className={messageType === 'whatsapp' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                    data-testid="message-type-whatsapp"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />{t('msgTypeWhatsapp')}
                  </Button>
                  <Button
                    type="button"
                    variant={messageType === 'email' ? 'default' : 'outline'}
                    onClick={() => { setMessageType('email'); setAiMessage(''); }}
                    className={messageType === 'email' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    data-testid="message-type-email"
                  >
                    <Mail className="w-4 h-4 mr-2" />{t('msgTypeEmail')}
                  </Button>
                </div>
              </div>

              {/* Tone */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500">{t('messageTone')}</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="message-tone-group">
                  {[
                    { val: 'polite', icon: Smile, labelKey: 'toneePolite', helpKey: 'toneePoliteHelp' },
                    { val: 'direct', icon: Zap, labelKey: 'toneDirect', helpKey: 'toneDirectHelp' },
                    { val: 'negotiation', icon: Handshake, labelKey: 'toneNegotiation', helpKey: 'toneNegotiationHelp' },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    const active = tone === opt.val;
                    return (
                      <button
                        key={opt.val}
                        type="button"
                        onClick={() => { setTone(opt.val); setAiMessage(''); }}
                        className={`flex flex-col items-center gap-1 px-2 py-2 rounded-md border text-xs transition-colors ${
                          active
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                        data-testid={`message-tone-${opt.val}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-medium">{t(opt.labelKey)}</span>
                        <span className="text-[10px] text-slate-400 leading-tight text-center hidden sm:block">{t(opt.helpKey)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Language */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500">{t('messageLanguage')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant={language === 'en' ? 'default' : 'outline'} onClick={() => { setLanguage('en'); setAiMessage(''); }} data-testid="message-lang-en">
                    {t('english')}
                  </Button>
                  <Button type="button" variant={language === 'fr' ? 'default' : 'outline'} onClick={() => { setLanguage('fr'); setAiMessage(''); }} data-testid="message-lang-fr">
                    {t('french')}
                  </Button>
                </div>
              </div>
            </div>

            {generating && (
              <div className="flex flex-col items-center justify-center py-8" data-testid="message-generating">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-2" />
                <p className="text-sm text-slate-600">{t('generatingMessage')}</p>
              </div>
            )}

            {!generating && aiMessage && (
              <div className="space-y-2" data-testid="generated-message-block">
                <Label className="text-xs uppercase tracking-wide text-slate-500">{t('editMessage')}</Label>
                <Textarea
                  value={aiMessage}
                  onChange={(e) => setAiMessage(e.target.value)}
                  className="min-h-[180px] sm:min-h-[220px] font-mono text-sm resize-none"
                  data-testid="generated-message"
                />
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 p-4 sm:p-6 bg-white">
            {!aiMessage && !generating && (
              <Button onClick={generate} className="w-full bg-blue-600 hover:bg-blue-700 h-11" data-testid="generate-with-lang-button">
                <MessageSquare className="w-4 h-4 mr-2" />{t('generateMessage')}
              </Button>
            )}
            {!generating && aiMessage && (
              <div className="flex flex-col gap-2">
                <div className="flex flex-col-reverse sm:flex-row gap-2">
                  <Button variant="outline" onClick={generate} className="flex-1" data-testid="regenerate-button">
                    <RefreshCw className="w-4 h-4 mr-2" />{t('regenerate')}
                  </Button>
                  <Button onClick={copyToClipboard} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="copy-message-button">
                    <Copy className="w-4 h-4 mr-2" />{copied ? t('copied') : t('copyToClipboard')}
                  </Button>
                </div>
                <Button variant="ghost" size="sm" onClick={openSaveTpl} className="text-slate-600 hover:text-slate-900" data-testid="save-as-template-button">
                  <Save className="w-3.5 h-3.5 mr-1.5" />{t('saveAsTemplate')}
                </Button>
                {/* "Mark as contacted" CTA — appears only after copy, when supplier is saved & not yet contacted */}
                {showMarkContactedCta && (
                  <Button
                    onClick={markAsContacted}
                    disabled={markingContacted}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="mark-as-contacted-button"
                  >
                    {markingContacted
                      ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{t('markAsContacted')}</>
                      : <><BadgeCheck className="w-3.5 h-3.5 mr-1.5" />{t('markAsContacted')}</>}
                  </Button>
                )}
                {hasMarked && (
                  <p className="text-xs text-emerald-700 flex items-center gap-1.5" data-testid="mark-as-contacted-confirm">
                    <BadgeCheck className="w-3.5 h-3.5" />{t('markedContacted')}
                  </p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <TemplatePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        supplierContext={supplierContext}
        onApply={onApplyTemplate}
      />

      <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
        <DialogContent className="sm:max-w-md" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle>{t('saveAsTemplate')}</DialogTitle>
            <DialogDescription className="sr-only">{t('saveAsTemplate')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500">{t('templateName')}</Label>
              <Input value={saveTplName} onChange={(e) => setSaveTplName(e.target.value)} placeholder={t('templateNamePlaceholder')} data-testid="save-template-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500">{t('templateCategory')}</Label>
              <select
                value={saveTplCategory}
                onChange={(e) => setSaveTplCategory(e.target.value)}
                className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="save-template-category"
              >
                <option value="price_inquiry">{t('cat_price_inquiry')}</option>
                <option value="moq_negotiation">{t('cat_moq_negotiation')}</option>
                <option value="shipping_inquiry">{t('cat_shipping_inquiry')}</option>
                <option value="sample_request">{t('cat_sample_request')}</option>
                <option value="branding">{t('cat_branding')}</option>
                <option value="general_intro">{t('cat_general_intro')}</option>
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setSaveTplOpen(false)} className="flex-1">{t('cancel')}</Button>
              <Button onClick={saveAsTemplate} disabled={savingTpl} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-template-confirm">
                {savingTpl && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t('save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MessageDialog;
